import React, { useState, useCallback, useEffect, useMemo } from "react"
import { VariantRenderProps } from "../Common/Render/Render"
import { Container } from "../Common/Container/Container"
import { JSONInput } from "../Common/JSON/JSON"
import { InputLabel } from "dash-styled"
import { Errors } from "../../../Common/Errors/Errors"
import { SDK } from "../Common/SDK/SDK"
import { JSONButtons } from "../Common/JSON/JSONButtons"
import { CSVInput } from "../Common/CSV/CSV"
import { CSVButtons } from "../Common/CSV/CSVButtons"
import {
    JobSubmitData,
    fromInputs,
    convertInputErrorPath
} from "../../../../../../Models/Client/JobSubmitData"
import {
    isJSONSchema7,
    isUploadableType,
    isSimpleType
} from "universal-schema/lib"
import { InputSchemaForm } from "../InputSchemaForm/InputSchemaForm"
import { FileType } from "../../../../../../Models/Client/FileType"
import { Uploadable } from "./Uploadable/Uploadable"

interface Props extends VariantRenderProps {
    orgUsername: string
    appID: string | undefined
    schemaType?: FileType
    uploaderStateKey: string
    initialJsonValue: unknown[]
}

export const SingleField: React.FC<Props> = ({
    orgUsername,
    appID,
    id,
    schema,
    data,
    uiSchema,
    onChangeMode,
    activeMode,
    onChange,
    errors,
    testId,
    single,
    schemaType,
    parseState,
    freshImport,
    uploaderStateKey,
    initialJsonValue,
    initiateSubmit,
    stopSubmit,
    onLoadedImport,
    onDownloadCSVExample,
    onDownloadJSONExample,
    onClearFile,
    onDropFile,
    handleUploadBlob
}: Props) => {
    const [dirty, setDirty] = useState<boolean>(false)
    const [prop, propSchema] = useMemo(
        () => Object.entries(schema.properties ?? {})[0],
        [schema]
    )
    const propTitle = isJSONSchema7(propSchema) ? propSchema.title : undefined
    const propDescription = isJSONSchema7(propSchema)
        ? propSchema.description
        : undefined
    const type = useMemo(
        () =>
            isJSONSchema7(propSchema)
                ? isUploadableType(uiSchema, prop)
                    ? "upload"
                    : isSimpleType(propSchema)
                    ? "simple"
                    : "complex"
                : null,
        [propSchema, uiSchema, prop]
    )
    const allowCSV = type !== "complex"

    const handleChange = useCallback(
        (newValue: JobSubmitData, errors?: unknown[]) => {
            if (JSON.stringify(newValue) !== JSON.stringify(data)) {
                if (!dirty) setDirty(true)
                onChange(newValue, errors)
            }
        },
        [data, dirty, onChange]
    )

    const handleSubmit = useCallback(
        (data: JobSubmitData, errors?: unknown[], canSubmit?: boolean) => {
            onChange(data, errors, canSubmit)
        },
        [onChange]
    )

    const handleChangeUpload = useCallback(
        (newValue?: (string | undefined)[]) => {
            if (!newValue) return
            const inputs = newValue.flatMap(maybeUrl =>
                maybeUrl === undefined ? [] : [{ [prop]: maybeUrl }]
            )

            handleChange(fromInputs(inputs, single))
        },
        [handleChange, prop, single]
    )
    const errorsForDisplay = useMemo(
        () =>
            errors
                .filter(
                    ({ keyword }) => type !== "upload" || keyword !== "minItems"
                )
                .flatMap(convertInputErrorPath),
        [errors, type]
    )

    useEffect(() => {
        /**
         * Complex type has no "input" version, so if it's on this version,
         * trigger a change to "json" mode
         */
        if (type === "complex" && activeMode === "input") {
            onChangeMode("json")
        }
    }, [activeMode, onChangeMode, type])

    useEffect(() => {
        if (
            type === "upload" &&
            initiateSubmit &&
            data !== undefined &&
            (data.type === "upload" ||
                (data.type === "raw" && data.jobs.length > 0))
        ) {
            handleSubmit(data, [], true)
        }
    }, [handleSubmit, data, type, initiateSubmit])

    if (!propSchema || !type) return null

    if (activeMode === "sdk") {
        if (appID === undefined) {
            return null
        }

        return (
            <Container
                onClick={onChangeMode}
                active={activeMode}
                variant={type}
                noSDKMode={false}
                testId={testId}
                noPanel={true}
                single={single}
                allowCSV={allowCSV}
            >
                <SDK appID={appID} schema={schema} testId={testId} />
            </Container>
        )
    }

    const meta = () => {
        if (
            type === "upload" ||
            (type === "simple" &&
                (activeMode === "json" || activeMode === "csv")) ||
            (type === "complex" && activeMode === "json")
        ) {
            const title =
                type === "complex" || propTitle === undefined
                    ? activeMode === "csv"
                        ? "Your CSV file"
                        : "Your data"
                    : propTitle
            const desc =
                type === "complex" || propDescription === undefined
                    ? undefined
                    : propDescription
            return (
                <div className="p-6 pb-0">
                    {activeMode === "json" && (
                        <>
                            <JSONButtons
                                onImport={onDropFile}
                                layout="absolute"
                                single={single}
                                orgUsername={orgUsername}
                                onDownloadExample={onDownloadJSONExample}
                            />
                            <InputLabel title="Your data" isValid={false} />
                        </>
                    )}
                    {activeMode === "csv" && (
                        <>
                            <CSVButtons
                                layout="absolute"
                                single={single}
                                orgUsername={orgUsername}
                                onDownloadExample={onDownloadCSVExample}
                            />
                            <InputLabel title="Your CSV file" isValid={false} />
                        </>
                    )}
                    {activeMode !== "csv" && activeMode !== "json" && (
                        <InputLabel
                            title={title}
                            description={desc}
                            isValid={dirty && errors.length === 0}
                        />
                    )}
                </div>
            )
        }
    }

    const json = () => (
        <JSONInput
            single={single}
            parseState={parseState}
            onChange={handleChange}
            testId={testId}
            errors={errorsForDisplay}
            data={data}
            initialValue={initialJsonValue}
            freshImport={freshImport}
            onLoadedImport={onLoadedImport}
            onClearFile={onClearFile}
            onDownloadExample={onDownloadJSONExample}
        />
    )

    const input = () => {
        switch (type) {
            case "simple":
                return (
                    <InputSchemaForm
                        testId={testId}
                        single={single}
                        schema={schema}
                        data={data}
                        uiSchema={uiSchema}
                        blobUploader={handleUploadBlob}
                        onChange={handleChange}
                        initiateSubmit={initiateSubmit}
                        stopSubmit={stopSubmit}
                        onSubmit={handleSubmit}
                    />
                )
            case "upload":
                return (
                    <Uploadable
                        type={schemaType}
                        stateKey={uploaderStateKey}
                        onChange={handleChangeUpload}
                        testId={testId}
                        single={single}
                    />
                )
            case "complex":
                return json()
        }
    }

    const csv = () => {
        switch (type) {
            case "complex":
                return json()
            case "simple":
            case "upload":
                return (
                    <CSVInput
                        testId={testId}
                        single={single}
                        parseState={parseState}
                        onClearFile={onClearFile}
                        onDropFile={onDropFile}
                        onDownloadExample={onDownloadCSVExample}
                    />
                )
        }
    }

    const inner = () => {
        switch (activeMode) {
            case "csv":
                return csv()
            case "json":
                return json()
            case "input":
                return input()
        }
    }

    const errorsEl = () => {
        // User cannot cause `minItems` error in upload mode

        if (
            !dirty ||
            errorsForDisplay.length === 0 ||
            parseState.type !== "idle"
        ) {
            return
        } else if (
            (activeMode === "input" || activeMode === "csv") &&
            type === "simple"
        ) {
            return
        } else {
            return (
                <Errors
                    errors={errorsForDisplay}
                    className="px-6"
                    showDataPath={activeMode === "json"}
                />
            )
        }
    }

    return (
        <Container
            onClick={onChangeMode}
            active={activeMode}
            variant={type}
            noSDKMode={appID === undefined}
            testId={testId}
            single={single}
            allowCSV={allowCSV}
        >
            {meta()}
            {activeMode !== "json" && errorsEl()}
            {inner()}
        </Container>
    )
}
